import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Users, Percent } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { isRocketInstance } from '@/config/instanceProfile'
import { PartnerPyramidFields } from '@/components/Partners/Modals/PartnerPyramidFields'
import { PARTNER_COMMISSION_MODE_OPTIONS } from '@/components/Partners/Modals/partnerDetailModalConstants'
import { cn } from '@/utils/cn'
import type { Partner } from '@/types'
import type { PartnerFormState } from '@/components/Partners/partnerView.types'

interface PartnerFormModalProps {
  open: boolean
  onClose: () => void
  formData: PartnerFormState
  setFormData: (data: PartnerFormState) => void
  onSave: (e: React.FormEvent) => void
  saving?: boolean
  partners?: Partner[]
  comerciais?: { id: string; name: string }[]
}

export function PartnerFormModal({ open, onClose, formData, setFormData, onSave, saving, partners = [], comerciais }: PartnerFormModalProps) {
  const [pyramidValid, setPyramidValid] = useState(true)

  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden'
    else document.body.style.overflow = ''
    return () => { document.body.style.overflow = '' }
  }, [open])

  useEffect(() => {
    if (open) setPyramidValid(true)
  }, [open])

  if (!open) return null

  const handleSubmit = (e: React.FormEvent) => {
    if (isRocketInstance && !pyramidValid) {
      e.preventDefault()
      return
    }
    onSave(e)
  }

  return createPortal(
    <AnimatePresence>
      <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className='absolute inset-0 bg-black/60 backdrop-blur-sm'
          onClick={onClose}
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 8 }}
          transition={{ duration: 0.15, ease: 'easeOut' }}
          className='relative w-full max-w-2xl max-h-[90vh] z-10 bg-[var(--bg-card)] border border-[var(--glass-border)] rounded-xl shadow-2xl flex flex-col overflow-hidden'
        >
          {/* Header */}
          <div className='flex items-center justify-between p-5 border-b border-[var(--glass-border)]'>
            <div className='flex items-center gap-3'>
              <div className='w-10 h-10 rounded-lg bg-[var(--brand-primary-muted)] flex items-center justify-center'>
                <Users size={18} className='text-[var(--brand-primary)]' />
              </div>
              <div>
                <h2
                  className='font-semibold text-[var(--font-color-h2)]'
                  style={{ fontSize: 'var(--font-size-h3)' }}
                >
                  {formData.id ? 'Editar Parceiro' : 'Novo Parceiro'}
                </h2>
                <p
                  className='text-[var(--font-color-muted)]'
                  style={{ fontSize: 'var(--font-size-xs)' }}
                >
                  Gestão de Ecossistema e Alianças
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className='p-1.5 rounded-md text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--glass-bg)] transition-colors'
            >
              <X size={16} />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className='min-h-0 flex-1 overflow-y-auto p-5 space-y-6'>
            {/* Perfil */}
            <div>
              <h4
                className='text-[var(--font-color-label)] uppercase tracking-wider font-medium mb-3'
                style={{ fontSize: 'var(--font-size-label)' }}
              >
                Perfil e Identificação
              </h4>
              <div className='grid grid-cols-2 gap-3'>
                <Input
                  label='Nome Completo *'
                  required
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  placeholder='Ex: Agência Digital'
                />
                <Input
                  label='CPF / CNPJ'
                  value={formData.cnpj}
                  onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
                  placeholder='00.000.000/0000-00'
                />
                <Input
                  label='Empresa Vinculada'
                  value={formData.empresa}
                  onChange={(e) => setFormData({ ...formData, empresa: e.target.value })}
                  placeholder='Se houver empresa vinculada'
                />
                <Input
                  label='Responsável'
                  value={formData.responsavel}
                  onChange={(e) => setFormData({ ...formData, responsavel: e.target.value })}
                  placeholder='Nome do contato principal'
                />
                <Input
                  label='Telefone do Contato'
                  value={formData.telefoneContato}
                  onChange={(e) => setFormData({ ...formData, telefoneContato: e.target.value })}
                  placeholder='(00) 0 0000-0000'
                />
                {comerciais && (
                  <label className='flex flex-col gap-1 text-xs text-[var(--font-color-label)]'>
                    Comercial da Rocket
                    <select
                      className='rounded-md border border-[var(--glass-border)] bg-transparent px-3 py-2 text-sm text-[var(--font-color-body)]'
                      value={formData.responsavelUserId}
                      onChange={(e) => setFormData({ ...formData, responsavelUserId: e.target.value })}
                    >
                      <option value=''>Sem comercial</option>
                      {comerciais.map((comercial) => (
                        <option key={comercial.id} value={comercial.id}>{comercial.name}</option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
            </div>

            {/* Contato */}
            <div>
              <h4
                className='text-[var(--font-color-label)] uppercase tracking-wider font-medium mb-3'
                style={{ fontSize: 'var(--font-size-label)' }}
              >
                Canais de Comunicação
              </h4>
              <div className='grid grid-cols-2 gap-3'>
                <Input
                  label='E-mail *'
                  required
                  type='email'
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder='parceiro@email.com'
                />
                <Input
                  label='WhatsApp / Telefone *'
                  required
                  value={formData.telefone}
                  onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                  placeholder='(00) 0 0000-0000'
                />
              </div>
              <p className='mt-2 text-[11px] leading-relaxed text-[var(--font-color-muted)]'>
                Ao salvar um parceiro novo, ele recebe um e-mail com o link para confirmar estes dados e aceitar as regras do programa.
              </p>
            </div>

            {/* Financeiro */}
            <div>
              <h4
                className='text-[var(--font-color-label)] uppercase tracking-wider font-medium mb-3'
                style={{ fontSize: 'var(--font-size-label)' }}
              >
                Setup Financeiro
              </h4>
              {isRocketInstance && (
                <div className='mb-3'>
                  <span className='block text-[var(--font-color-label)] text-xs font-medium mb-1.5'>
                    Regra padrão do parceiro externo *
                  </span>
                  <p className='mb-2 text-[11px] leading-relaxed text-[var(--font-color-muted)]'>
                    V2 separa parceiro externo, parceiro líder, comercial Rocket e prêmios em lançamentos independentes.
                    Este campo define só a regra padrão do parceiro; a negociação pode sobrescrever.
                  </p>
                  <div className='grid grid-cols-1 gap-2 sm:grid-cols-2' role='radiogroup' aria-label='Regra padrão do parceiro externo'>
                    {PARTNER_COMMISSION_MODE_OPTIONS.map((option) => {
                      const selected = formData.modoComissao === option.id
                      return (
                        <button
                          key={option.id}
                          type='button'
                          role='radio'
                          aria-checked={selected}
                          onClick={() =>
                            setFormData({
                              ...formData,
                              modoComissao: option.id,
                              ...(option.id === 'DUAS_MENSALIDADES' ? { comissao: 100 } : {}),
                            })
                          }
                          className={cn(
                            'rounded-lg border px-3 py-2.5 text-left transition-colors',
                            selected
                              ? 'border-[var(--brand-primary)] bg-[var(--brand-primary-muted)]'
                              : 'border-[var(--border)] bg-[var(--bg-main)] hover:border-[var(--text-muted)]',
                          )}
                        >
                          <span
                            className={cn(
                              'block text-sm font-semibold',
                              selected ? 'text-[var(--brand-primary)]' : 'text-[var(--text-primary)]',
                            )}
                          >
                            {option.label}
                          </span>
                          <span className='mt-0.5 block text-[11px] leading-snug text-[var(--font-color-muted)]'>
                            {option.hint}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}
              <div className='grid grid-cols-2 gap-3'>
                <div className='relative'>
                  <Input
                    label='Comissão Acordada (%) *'
                    required
                    type='number'
                    step='0.1'
                    value={formData.comissao}
                    onChange={(e) =>
                      setFormData({ ...formData, comissao: e.target.value === '' ? '' : Number(e.target.value) })
                    }
                  />
                  <Percent size={14} className='absolute right-3 top-[38px] text-[var(--text-muted)]' />
                  {isRocketInstance && formData.modoComissao === 'DUAS_MENSALIDADES' && (
                    <p className='mt-1 text-[11px] text-[var(--font-color-muted)]'>
                      Nesta regra, o indicador recebe 100% da primeira parcela somente. O percentual recorrente não se aplica.
                    </p>
                  )}
                </div>
                <Input
                  label='Chave PIX para Repasses'
                  value={formData.chavePix}
                  onChange={(e) => setFormData({ ...formData, chavePix: e.target.value })}
                  placeholder='CPF, Email, Telefone ou Aleatória'
                />
              </div>
            </div>

            {isRocketInstance && (
              <PartnerPyramidFields
                formData={formData}
                setFormData={(data) => setFormData({ ...data, responsavelUserId: formData.responsavelUserId })}
                partners={partners}
                onValidityChange={setPyramidValid}
              />
            )}

            <button type='submit' className='hidden' />
          </form>

          {/* Footer */}
          <div className='flex items-center justify-end gap-2 px-5 py-4 border-t border-[var(--glass-border)]'>
            <Button variant='ghost' size='sm' onClick={onClose}>
              Descartar
            </Button>
            <Button
              variant='primary'
              size='sm'
              loading={saving}
              disabled={isRocketInstance && !pyramidValid}
              onClick={handleSubmit}
            >
              {formData.id ? 'Salvar Alterações' : 'Confirmar Cadastro'}
            </Button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  )
}
